import { ImagePlus } from 'lucide-react';
import { type DragEvent, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'v5/platform/dom/cn';
import { useFileInput } from 'v5/platform/react/useFileInput';

function draggingFiles(event: DragEvent): boolean {
  return event.dataTransfer.types.includes('Files');
}

/** The files of a drop; some browsers list them only among the items. */
function droppedFiles(transfer: DataTransfer): File[] {
  if (transfer.files.length > 0) return [...transfer.files];
  return [...transfer.items]
    .filter(item => item.kind === 'file')
    .map(item => item.getAsFile())
    .filter(file => file !== null);
}

/**
 * Where files are dropped or picked from when there are none yet. With a `preview` it shows that
 * in place of the words (still a drop target), until a file is dragged over it.
 */
export function MediaDropZone({
  accept,
  multiple,
  description,
  preview,
  onFiles
}: {
  accept?: string;
  multiple: boolean;
  description: ReactNode;
  preview?: ReactNode;
  onFiles(files: File[]): void;
}) {
  const { t } = useTranslation('patterns', {
    keyPrefix: 'ImageCollectionUploadField'
  });
  /** A file dragged in from outside is over the zone. */
  const [fileOver, setFileOver] = useState(false);
  const openPicker = useFileInput({
    accept,
    multiple,
    onFilesSelected: onFiles
  });
  const dropHandlers = {
    onDragEnter: (event: DragEvent) => {
      if (draggingFiles(event)) event.preventDefault();
    },
    onDragOver: (event: DragEvent) => {
      if (!draggingFiles(event)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
      setFileOver(true);
    },
    // moving onto a child of the zone is not leaving it
    onDragLeave: (event: DragEvent) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
        setFileOver(false);
      }
    },
    onDrop: (event: DragEvent) => {
      if (!draggingFiles(event)) return;
      event.preventDefault();
      setFileOver(false);
      onFiles(droppedFiles(event.dataTransfer));
    }
  };
  const frame = cn(
    'flex w-full flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border-default bg-bg-subtle px-6 py-10 text-center outline-none transition-colors',
    fileOver && 'border-brand-500 bg-brand-50'
  );
  const words = (
    <>
      <span className="flex size-12 items-center justify-center rounded-full bg-bg-default text-fg-muted shadow-sm ring-1 ring-border-default">
        <ImagePlus className="size-6" />
      </span>
      <span className="flex flex-col gap-1">
        <span className="text-base font-medium text-fg-default">
          {fileOver
            ? t('dropToAdd')
            : multiple
              ? t('emptyTitle')
              : t('emptyTitleSingle')}
        </span>
        <span className="text-sm text-fg-muted first-letter:uppercase">
          {description}
        </span>
      </span>
    </>
  );
  return (
    // Room for the focus ring inside any scroller around the zone.
    <div className="-m-[4px] p-[4px]">
      {preview ? (
        // the words stay, unseen, so the zone keeps its height under the preview; the same
        // element all through a drag, or it would be torn down under the pointer
        <div {...dropHandlers} className={cn(frame, 'relative')}>
          <span
            aria-hidden={!fileOver}
            className={cn('contents', !fileOver && 'invisible')}
          >
            {words}
          </span>
          {!fileOver && <div className="absolute inset-2">{preview}</div>}
        </div>
      ) : (
        <button
          type="button"
          onClick={openPicker}
          {...dropHandlers}
          className={cn(
            frame,
            'hover:border-bg-inset hover:bg-bg-subtle-2',
            'ring-offset-bg-default focus-visible:ring-2 focus-visible:ring-bg-emphasis/70 focus-visible:ring-offset-2'
          )}
        >
          {words}
        </button>
      )}
    </div>
  );
}
